import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { adaptEchartsOptions, BaseEchartsDirective } from '@examdojo/core/widget';
import type { EChartsOption } from 'echarts';
import { LineChart, ScatterChart } from 'echarts/charts';
import { GridComponent, MarkLineComponent, TitleComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { NgxEchartsDirective, provideEchartsCore } from 'ngx-echarts';

import { tPdf, tInvCdf } from '../../tests/test-runner/distributions.util';

echarts.use([LineChart, ScatterChart, GridComponent, MarkLineComponent, TitleComponent]);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const Z_DF = 100_000; // t(100k) ≈ standard normal
const X_MIN = -4;
const X_MAX = 4;
const N_POINTS = 300;

const COLOR_CURVE = '#1e40af';
const COLOR_FILL = 'rgba(147, 197, 253, 0.35)';
const COLOR_REJECT = 'rgba(139, 92, 246, 0.3)';
const COLOR_REJECT_LINE = '#8b5cf6';
const COLOR_ACCEPT = 'rgba(147, 197, 253, 0.15)';
const COLOR_MUTED = '#9ca3af';

// ---------------------------------------------------------------------------
// Chart builder
// ---------------------------------------------------------------------------

function buildNormalChart(alpha: number | null): EChartsOption {
  const allPoints: Array<[number, number]> = [];
  let peakY = 0;
  for (let i = 0; i <= N_POINTS; i++) {
    const x = X_MIN + (i / N_POINTS) * (X_MAX - X_MIN);
    const y = tPdf(x, Z_DF);
    allPoints.push([x, y]);
    if (y > peakY) {
      peakY = y;
    }
  }

  const series: Array<Record<string, unknown>> = [];

  if (alpha !== null && alpha > 0 && alpha < 1) {
    // Show rejection/acceptance regions for this alpha (two-tailed)
    const zCrit = tInvCdf(1 - alpha / 2, Z_DF);
    const leftCv = -zCrit;
    const rightCv = zCrit;

    const leftReject = allPoints.filter(([x]) => x <= leftCv);
    const acceptRegion = allPoints.filter(([x]) => x >= leftCv && x <= rightCv);
    const rightReject = allPoints.filter(([x]) => x >= rightCv);

    series.push(makeArea(leftReject, COLOR_REJECT, COLOR_CURVE));
    series.push(makeArea(acceptRegion, COLOR_ACCEPT, COLOR_CURVE));
    series.push(makeArea(rightReject, COLOR_REJECT, COLOR_CURVE));

    // Mark lines for critical values
    const markLineData: Array<Record<string, unknown>> = [
      cvMarkLine(-zCrit, `z = −${zCrit.toFixed(3)}`),
      cvMarkLine(zCrit, `z = ${zCrit.toFixed(3)}`),
    ];

    (series[0] as Record<string, unknown>)['markLine'] = {
      symbol: 'none',
      animation: false,
      data: markLineData,
    };

    // Annotations
    const confPct = Math.round((1 - alpha) * 100);
    series.push(annotation(0, peakY * 0.45, `${confPct}%`, '#1e40af'));
    series.push(annotation((X_MIN + leftCv) / 2, peakY * 0.15, `α/2 = ${(alpha / 2).toFixed(3)}`, COLOR_REJECT_LINE));
    series.push(annotation((rightCv + X_MAX) / 2, peakY * 0.15, `α/2 = ${(alpha / 2).toFixed(3)}`, COLOR_REJECT_LINE));
  } else {
    // Default: show full curve with 68-95-99.7 bands
    series.push(makeArea(allPoints, COLOR_FILL, COLOR_CURVE));

    // σ-band annotations
    series.push(bracketAnnotation(peakY * 0.78, '68.26%'));
    series.push(bracketAnnotation(peakY * 0.92, '95.44%'));
    series.push(bracketAnnotation(peakY * 1.06, '99.72%'));
  }

  return {
    grid: { left: 40, right: 20, top: 16, bottom: 50 },
    xAxis: {
      type: 'value' as const,
      min: X_MIN,
      max: X_MAX,
      splitNumber: 8,
      axisLine: { lineStyle: { color: '#d1d5db' } },
      axisTick: { lineStyle: { color: '#d1d5db' } },
      axisLabel: {
        fontSize: 10,
        color: COLOR_MUTED,
        formatter: (v: number) => {
          if (v === 0) {
            return 'μ';
          }
          if (v === -3) {
            return 'μ−3σ';
          }
          if (v === -2) {
            return 'μ−2σ';
          }
          if (v === -1) {
            return 'μ−σ';
          }
          if (v === 1) {
            return 'μ+σ';
          }
          if (v === 2) {
            return 'μ+2σ';
          }
          if (v === 3) {
            return 'μ+3σ';
          }
          return '';
        },
      },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value' as const,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: { show: false },
    },
    series,
    animation: false,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeArea(data: Array<[number, number]>, fill: string, line: string): Record<string, unknown> {
  return {
    type: 'line',
    data,
    smooth: false,
    symbol: 'none',
    lineStyle: { color: line, width: 2 },
    areaStyle: {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        { offset: 0, color: fill },
        { offset: 1, color: fill.replace(/[\d.]+\)$/, '0.02)') },
      ]),
    },
  };
}

function cvMarkLine(xVal: number, label: string): Record<string, unknown> {
  return {
    xAxis: xVal,
    lineStyle: { color: COLOR_REJECT_LINE, type: 'dashed' as const, width: 1.5 },
    label: {
      formatter: label,
      position: 'insideEndBottom' as const,
      fontSize: 10,
      color: COLOR_REJECT_LINE,
    },
  };
}

function annotation(x: number, y: number, text: string, color: string): Record<string, unknown> {
  return {
    type: 'scatter',
    data: [[x, y]],
    symbolSize: 0,
    label: {
      show: true,
      formatter: text,
      fontSize: 11,
      fontWeight: 600,
      color,
      backgroundColor: 'rgba(255,255,255,0.85)',
      padding: [3, 6],
      borderRadius: 3,
    },
    z: 10,
    silent: true,
  };
}

function bracketAnnotation(y: number, text: string): Record<string, unknown> {
  return {
    type: 'scatter',
    data: [[0, y]],
    symbolSize: 0,
    label: {
      show: true,
      formatter: text,
      fontSize: 10,
      fontWeight: 500,
      color: '#6b7280',
      backgroundColor: 'rgba(255,255,255,0.85)',
      padding: [2, 5],
      borderRadius: 3,
    },
    z: 10,
    silent: true,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

@Component({
  selector: 'dojo-normal-distribution-diagram',
  standalone: true,
  imports: [NgxEchartsDirective],
  providers: [provideEchartsCore({ echarts })],
  templateUrl: './normal-distribution-diagram.component.html',
  styleUrl: './normal-distribution-diagram.component.scss',
  host: { class: 'block' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NormalDistributionDiagramComponent extends BaseEchartsDirective {
  readonly highlightAlpha = input<number | null>(null);

  readonly chartOptions = computed<EChartsOption>(() => {
    return buildNormalChart(this.highlightAlpha());
  });

  readonly adaptedOptions = computed(() => adaptEchartsOptions(this.chartOptions(), this.document, this.isDark()));
}
